import { Injectable } from '@angular/core';

import {
  FeatureTourScenarioConfig,
  FeatureTourStep,
  FeatureTourStepConfig,
  FeatureTourStepContent,
  TourScenarioRecord,
  TourScenarioSummary,
} from './tour.types';

type ScenarioMap = Map<string, TourScenarioRecord>;

const TIMESHEET_DEMO_ROUTE =
  '/main/projects/project-details/e2c28fcf-2861-40f5-8bd0-d864cea89322/time-sheet?phasesIds%5B%5D=5b51ebf5-375f-4cb2-83ec-ea03bee2f6b4&phasesIds%5B%5D=c5c5ee04-e17d-401b-b897-1d023afdc946&from=2026-05-04&to=2026-05-10&mergedEmployees=true';

@Injectable({ providedIn: 'root' })
export class TourConfigService {
  private readonly scenarios: TourScenarioRecord[];
  private readonly visibleScenarios: TourScenarioRecord[];
  private readonly scenariosJson: string;
  private readonly scenarioMap: ScenarioMap;

  constructor() {
    this.scenarios = this.buildScenarioRecords(RAW_FEATURE_TOURS);
    this.visibleScenarios = this.scenarios.filter((scenario) => scenario.visibleInQuickGuide);
    this.scenariosJson = JSON.stringify(this.visibleScenarios);
    this.scenarioMap = new Map(this.scenarios.map((scenario) => [scenario.id, scenario]));
  }

  getScenariosJson(): string {
    return this.scenariosJson;
  }

  getScenarioRecords(): TourScenarioRecord[] {
    return this.visibleScenarios.map((scenario) => cloneScenarioRecord(scenario));
  }

  getScenarioSummaries(): TourScenarioSummary[] {
    return this.visibleScenarios.map(({ steps, ...rest }) => ({
      ...rest,
      tags: [...rest.tags],
      keywords: [...rest.keywords],
    }));
  }

  getScenarioById(id: string): TourScenarioRecord | undefined {
    const scenario = this.scenarioMap.get(id);
    if (!scenario) {
      return undefined;
    }

    return cloneScenarioRecord(scenario);
  }

  private buildScenarioRecords(configs: FeatureTourScenarioConfig[]): TourScenarioRecord[] {
    return configs
      .map((config) => this.buildScenario(config))
      .filter((scenario): scenario is TourScenarioRecord => scenario !== null);
  }

  private buildScenario(config: FeatureTourScenarioConfig): TourScenarioRecord | null {
    const steps = config.steps?.map((step) => normalizeStep(step)) ?? [];

    if (!steps.length) {
      return null;
    }

    const [firstStep, ...restSteps] = steps;
    const title = config.title ?? firstStep?.title ?? config.label ?? config.id;
    const sections = restSteps.map((step) => step.title || step.anchorId || 'Untitled step');

    return {
      id: config.id,
      feature: config.feature,
      label: config.label ?? title,
      description: config.description,
      createdAt: config.createdAt,
      order: config.order,
      tags: [...(config.tags ?? [])],
      keywords: [...(config.keywords ?? [])],
      demoReady: config.demoReady ?? false,
      visibleInQuickGuide: config.visibleInQuickGuide !== false,
      steps,
      title,
      sections,
      totalSteps: steps.length,
    };
  }
}

function cloneScenarioRecord(record: TourScenarioRecord): TourScenarioRecord {
  return {
    ...record,
    tags: [...record.tags],
    keywords: [...record.keywords],
    steps: record.steps.map((step) => cloneStep(step)),
  };
}

function cloneStep(step: FeatureTourStep): FeatureTourStep {
  return {
    ...step,
    offset: step?.offset ? { ...step.offset } : step?.offset,
    media: step?.media ? { ...step.media } : step?.media,
    backdropConfig: step?.backdropConfig ? { ...step.backdropConfig } : step?.backdropConfig,
    stepDimensions: step?.stepDimensions ? { ...step.stepDimensions } : step?.stepDimensions,
    placement: step?.placement ? { ...step.placement } : step?.placement,
    route: Array.isArray(step.route) ? [...step.route] : step.route,
    keywords: step?.keywords ? [...step.keywords] : step?.keywords,
    richContent: cloneRichContent(step.richContent),
  };
}

function cloneRichContent(content: FeatureTourStepContent | undefined): FeatureTourStepContent | undefined {
  if (!content) {
    return undefined;
  }

  return {
    shortDescription: content.shortDescription,
    longDescription: content.longDescription,
    impactOnSystem: content.impactOnSystem,
    risks: content.risks,
  };
}

function normalizeStep(stepConfig: FeatureTourStepConfig): FeatureTourStep {
  const { content, richContent, ...rest } = stepConfig;

  let normalizedContent: string | undefined;
  let normalizedRichContent: FeatureTourStepContent | undefined = cloneRichContent(richContent);

  if (typeof content === 'string') {
    normalizedContent = content;
  } else if (content) {
    normalizedRichContent = cloneRichContent(content);
    normalizedContent = content.shortDescription ?? normalizedContent;
  }

  if (!normalizedContent && normalizedRichContent) {
    const summaryParts = [
      normalizedRichContent.shortDescription,
      normalizedRichContent.longDescription,
      normalizedRichContent.impactOnSystem,
      normalizedRichContent.risks,
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    normalizedContent = summaryParts[0] ?? undefined;
  }

  return cloneStep({
    ...rest,
    content: normalizedContent,
    richContent: normalizedRichContent,
  });
}

const RAW_FEATURE_TOURS: FeatureTourScenarioConfig[] = [
  {
    id: 'timesheet-demo-control-week',
    feature: 'timesheet',
    label: 'Timesheet control week',
    title: 'Timesheet control week',
    description:
      'A client-demo walk-through showing how one row per employee, merged phase hours, fill status and imports help control a project week.',
    tags: ['timesheet', 'project', 'demo', 'merged-employees', 'import'],
    keywords: [
      'time sheet',
      'timesheet',
      'hours',
      'czas pracy',
      'godziny',
      'one row per employee',
      'merged employees',
      'phases',
      'fazy',
      'Docking',
      'After Docking',
      'Grabowski',
      'Miroslaw',
      'Mirosław',
      'Hull 6155',
      'import excel',
      'xlsx',
      'demo klient',
      'client demo',
    ],
    demoReady: true,
    visibleInQuickGuide: true,
    createdAt: '2026-05-15',
    order: 0,
    steps: [
      {
        anchorId: 'timesheet-phase-strip',
        stepId: 'timesheet-project-week',
        title: 'Timesheet project week',
        route: TIMESHEET_DEMO_ROUTE,
        delayAfterNavigation: 600,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: [
          'timesheet',
          'time sheet',
          'start timesheet demo',
          'timesheet overview',
          'phase carousel',
          'project week',
          'Hull 6155',
          'Docking',
          'After Docking',
        ],
        content: {
          shortDescription: `
            <div class="tour-story tour-story--timesheet">
              <div class="tour-story__kicker">Client demo · Hull 6155</div>
              <p><b>This week view combines two active phases:</b> Docking and After Docking. The guide starts from a prepared demo URL so every number is repeatable.</p>
              <div class="tour-metric-strip">
                <span><b>2</b> phases</span>
                <span><b>10</b> visible rows</span>
                <span><b>101h</b> project total</span>
              </div>
              <div class="tour-flow-visual" aria-hidden="true">
                <span class="tour-flow-visual__node">Docking</span>
                <span class="tour-flow-visual__node">After Docking</span>
                <span class="tour-flow-visual__connector"></span>
                <span class="tour-flow-visual__output">04.05-10.05</span>
              </div>
            </div>
          `,
          longDescription:
            '<p>The carousel is the business context: the same project week can contain multiple operational phases, and the user can focus the table without leaving the project.</p>',
          impactOnSystem:
            '<p>When phases are selected, the table, totals, fill status, exports and edit dialogs all use the same filtered context.</p>',
        },
        enableBackdrop: true,
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'timesheet-date-controls',
        stepId: 'timesheet-period',
        title: 'Lock the reporting window',
        route: TIMESHEET_DEMO_ROUTE,
        delayAfterNavigation: 400,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: ['date range', 'period', 'last week', '2026-05-04', '2026-05-10'],
        content: {
          shortDescription:
            '<p>The date range freezes the demo week: <b>04.05-10.05.2026</b>. This makes the walkthrough deterministic for training and client demos.</p>',
          impactOnSystem:
            '<p>The selected period is stored in the URL, so a support person can send the exact same view to another user.</p>',
        },
        enableBackdrop: true,
        placement: { xPosition: 'after', yPosition: 'below' },
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'timesheet-merged-toggle',
        stepId: 'timesheet-one-row-per-employee',
        title: 'One row per employee',
        route: TIMESHEET_DEMO_ROUTE,
        delayAfterNavigation: 400,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: ['one row per employee', 'mergedEmployees', 'merge duplicates', 'scalony widok'],
        content: {
          shortDescription:
            `<div class="tour-story tour-story--merge">
              <p><b>One row per employee</b> removes the duplicate-row problem when the same person works across selected phases.</p>
              <div class="tour-merge-visual" aria-hidden="true">
                <span>Docking row</span>
                <span>After Docking row</span>
                <b>Grabowski Miroslaw (520)</b>
              </div>
            </div>`,
          longDescription:
            '<p>For a project manager, this is the difference between scanning one operational workload line and mentally reconciling several phase rows.</p>',
          impactOnSystem:
            '<p>The URL carries <code>mergedEmployees=true</code>, so the merged view is shareable and reproducible.</p>',
        },
        enableBackdrop: true,
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'timesheet-demo-row',
        stepId: 'timesheet-grabowski-row',
        title: 'Merged workload row',
        route: TIMESHEET_DEMO_ROUTE,
        delayAfterNavigation: 500,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: ['Grabowski Miroslaw', 'natural id 520', '96 hours', 'daily worker', 'merged row'],
        content: {
          shortDescription: `
            <div class="tour-story tour-story--focus">
              <p><b>Grabowski Miroslaw (520)</b> is assigned to both demo phases. The merged row shows the real weekly picture without duplicate employee rows.</p>
              <div class="tour-metric-strip tour-metric-strip--contrast">
                <span><b>36h</b> Tue</span>
                <span><b>20h</b> Wed</span>
                <span><b>96h</b> week</span>
              </div>
            </div>
          `,
          impactOnSystem:
            '<p>The row total is calculated from both selected phases, which makes overloads and unusual days visible immediately.</p>',
          risks:
            '<p>For demo safety, inspect the row and dialogs read-only unless you intentionally test a save on throwaway data.</p>',
        },
        enableBackdrop: true,
        offset: { y: 8 },
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'timesheet-demo-cell',
        stepId: 'timesheet-hotspot-cell',
        title: 'The 36-hour signal',
        route: TIMESHEET_DEMO_ROUTE,
        delayAfterNavigation: 400,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: ['36 hours', '2026-05-05', 'cell', 'phase candidates', 'set timesheet'],
        content: {
          shortDescription:
            '<p>The <b>36</b> cell is the strongest demo moment: it is a single visible signal created by entries from two selected phases.</p>',
          longDescription:
            '<p>Clicking a cell opens the same operational edit dialog users already know. The walkthrough can demonstrate the control point without saving changes.</p>',
        },
        enableBackdrop: true,
        nextOnAnchorClick: true,
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'timesheet-phase-candidate-selector',
        stepId: 'timesheet-edit-dialog',
        title: 'Inspect before saving',
        route: TIMESHEET_DEMO_ROUTE,
        isAsync: true,
        asyncStepTimeout: 10000,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: ['set timesheet', 'edit dialog', 'read only', 'save disabled demo'],
        content: {
          shortDescription:
            '<p>The dialog collects hours, overtime, activity and extra cost context in one place. In the client demo we open it to show control depth, then close it without saving.</p>',
          impactOnSystem:
            '<p>This proves the guide can explain real operational decisions, not only static labels.</p>',
        },
        enableBackdrop: true,
        placement: { xPosition: 'before', yPosition: 'below' },
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'timesheet-set-dialog-close',
        stepId: 'timesheet-close-edit-dialog',
        title: 'Close without changing data',
        route: TIMESHEET_DEMO_ROUTE,
        isAsync: true,
        asyncStepTimeout: 10000,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: ['close dialog', 'no save', 'read only'],
        content: {
          shortDescription:
            '<p>Close the dialog here to keep the prepared fixture untouched. The demo remains repeatable for the next session.</p>',
        },
        enableBackdrop: true,
        nextOnAnchorClick: true,
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'timesheet-fill-status',
        stepId: 'timesheet-fill-status',
        title: 'Fill status explains data quality',
        route: TIMESHEET_DEMO_ROUTE,
        delayAfterNavigation: 400,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: ['fill status', 'in progress', 'missing days', 'data quality'],
        content: {
          shortDescription:
            '<p><b>Fill status</b> turns timesheet completeness into a visible quality signal. It tells the user whether the week is ready for the next process.</p>',
          impactOnSystem:
            '<p>The same status supports operational follow-up before reporting or payroll export.</p>',
        },
        media: {
          type: 'image',
          src: 'assets/tours/timesheet-fill-status-tooltip.gif',
          alt: 'Fill status hover showing missing timesheet dates grouped by employee',
          maxWidth: 370,
          height: 260,
        },
        enableBackdrop: true,
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'timesheet-import-action',
        stepId: 'timesheet-import-excel',
        title: 'Import Excel without hunting',
        route: TIMESHEET_DEMO_ROUTE,
        delayAfterNavigation: 400,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: ['import', 'excel', 'xlsx', 'template', 'workbook', 'timesheet file'],
        content: {
          shortDescription:
            `<div class="tour-story tour-story--import">
              <p>The import action is part of the same guided path, so users can discover workbook upload, template download and full workbook options without separate documentation.</p>
              <div class="tour-import-visual" aria-hidden="true">
                <span class="tour-import-visual__file">XLSX</span>
                <span class="tour-import-visual__arrow"></span>
                <span class="tour-import-visual__action">Import time sheet</span>
                <span class="tour-import-visual__check">validation</span>
              </div>
            </div>`,
          longDescription:
            '<p>For the demo, open the import dialog only if you want to show the choices; no file upload is needed for the basic walkthrough.</p>',
        },
        enableBackdrop: true,
        placement: { xPosition: 'before', yPosition: 'below' },
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'timesheet-total-hours',
        stepId: 'timesheet-closeout',
        title: 'Close with a business number',
        route: TIMESHEET_DEMO_ROUTE,
        delayAfterNavigation: 400,
        tourLabel: 'Timesheet control week',
        createdAt: '2026-05-15',
        keywords: ['total', '101 hours', 'summary', 'report'],
        content: {
          shortDescription:
            '<p>The walkthrough closes on the weekly total. This gives the client a practical story: select phases, verify workload, inspect anomalies, and move toward reporting.</p>',
          impactOnSystem:
            '<p>That is the value of the tutorial: it guides a user through the same operational path the system expects them to perform.</p>',
        },
        enableBackdrop: true,
        backdropConfig: { offset: 10 },
      },
    ],
  },
  {
    id: 'employees-list-tour-1',
    feature: 'employees',
    label: 'Employees list overview',
    tags: ['employees', 'list', 'overview'],
    keywords: ['employees', 'employee list', 'crew', 'availability', 'export', 'equipment rentals'],
    demoReady: true,
    visibleInQuickGuide: true,
    createdAt: '2024-10-17',
    order: 10,
    steps: [
      {
        anchorId: 'employees-list-intro',
        title: 'Overview',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>This short tutorial walks you through <b>the employees list</b> so you can quickly discover every option available here. Navigate using the buttons below or by clicking on the highlighted elements.</p><p>You can also open this tutorial anytime by clicking the "Start tutorial" button.</p>',
        },
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
      },
      {
        anchorId: 'main-navigation',
        title: 'Navigation menu',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Use the navigation icons to switch between employees, projects, reports and the admin view.</p>',
        },
        enableBackdrop: true,
        offset: { x: 20 },
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'main-search',
        title: 'Global search',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Search across the employee list by typing a name, surname or an employee ID. Results filter instantly while you type.</p>',
        },
        media: {
          type: 'video',
          src: 'assets/video3.webm',
          maxWidth: 320,
          height: 180,
        },
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'filters-section',
        title: 'Filter employees',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Combine filters to narrow down employees by profession, certificates, wages or custom rating categories. Use the button next to the filters to see more options.</p>',
        },
        media: {
          type: 'video',
          src: 'assets/video.webm',
          maxWidth: 320,
          height: 180,
        },
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'customise-columns',
        title: 'Customise columns',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Open column settings to decide which data should be visible and reorder columns to match your workflow.</p>',
        },
        media: {
          type: 'video',
          src: 'assets/video2.webm',
          maxWidth: 320,
          height: 180,
        },
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'table-sorting',
        title: 'Sort columns',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Click any column header to sort employees in ascending or descending order and quickly view the most important records.</p>',
        },
        enableBackdrop: true,
        media: {
          type: 'video',
          src: 'assets/video4.webm',
          maxWidth: 320,
          height: 180,
        },
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'employee-actions',
        title: 'Row actions',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>The contextual menu lets you edit an employee, assign them to a project, or remove them from the list.</p>',
        },
        enableBackdrop: true,
        showArrow: false,
        placement: { xPosition: 'before', yPosition: 'below' },
        offset: { x: -60 },
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'employee-checkbox',
        title: 'Select employees',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Use the checkboxes to select individual employees or click the header checkbox to select the entire page.</p>',
        },
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'selection-actions',
        title: 'Bulk actions',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>After selecting employees you can assign them to projects or remove them in one go using these buttons.</p>',
        },
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'add-employee-btn',
        stepId: 'add-employee',
        title: 'Add new employee',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Create a new employee profile using this button. You will be redirected to the details view to fill in their information.</p>',
        },
        enableBackdrop: true,
        placement: { xPosition: 'before', yPosition: 'below' },
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'check-availability-btn',
        title: 'Check availability',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Open the availability dialog to check who is free in a selected date range. Click the button to continue.</p>',
        },
        nextOnAnchorClick: true,
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'check-availability-calendar',
        title: 'Availability dialog',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Pick the start and end dates that interest you. You can navigate months using the arrows on the calendar header.</p>',
        },
        isAsync: true,
        placement: {
          xPosition: 'after',
          yPosition: 'below',
        },
        popoverClass: 'tour-bottom-offset',
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'check-availability-confirm',
        title: 'Confirm selection',
        route: 'main/employees/list',
        content: {
          shortDescription: '<p>Confirm the chosen dates to apply the availability filter and return to the list.</p>',
        },
        isAsync: true,
        nextOnAnchorClick: true,
        placement: {
          xPosition: 'after',
          yPosition: 'below',
        },
        popoverClass: 'tour-bottom-offset',
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'availability-results',
        title: 'Availability results',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Rows highlighted in red show employees who are unavailable for the selected dates. They are already assigned to a project.</p>',
        },
        enableBackdrop: true,
        offset: { y: 12 },
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'export-list-btn',
        title: 'Export list',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Generate a spreadsheet with employees data. The export includes all employees data, not only the visible columns.</p>',
        },
        enableBackdrop: true,
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'import-equipment-btn',
        title: 'Import equipment rentals',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>Upload the spreadsheet template to update equipment rental data in bulk instead of editing employees one by one.</p>',
        },
        enableBackdrop: true,
        placement: { xPosition: 'before', yPosition: 'below' },
        tourLabel: 'Employees list overview',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
    ],
  },
  {
    id: 'employees-list-tour-2',
    feature: 'employees',
    label: 'Employees quick actions',
    tags: ['employees', 'list', 'short'],
    demoReady: false,
    visibleInQuickGuide: false,
    createdAt: '2025-09-17',
    order: 2,
    steps: [
      {
        anchorId: 'employees-list-intro2',
        title: 'Overview',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>22222This short tutorial walks you through the employees list so you can quickly discover every option available here. Navigate using the buttons below or by clicking on the highlighted elements.</p><p>You can also open this tutorial anytime by clicking the "Start tutorial" button.</p>',
        },
        enableBackdrop: true,
        tourLabel: 'Employees quick actions',
        createdAt: '2024-10-17',
      },
      {
        anchorId: 'main-navigation',
        title: 'Navigation menu',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>22222Use the navigation icons to switch between employees, projects, reports and the admin view.</p>',
        },
        enableBackdrop: true,
        offset: { x: 20 },
        tourLabel: 'Employees quick actions',
        createdAt: '2024-10-17',
        backdropConfig: { offset: 10 },
      },
      {
        anchorId: 'projects-new-project-btn',
        title: 'Create new project',
        content: {
          shortDescription:
            '<p>22222Use this hhh shortcut to open the ęóąśłżźćń project creator and fill in the basic details for a new engagement. ggg ccc</p>',
        },
        enableBackdrop: true,
        route: '/main/projects/list',
        delayAfterNavigation: 400,
        isAsync: true,
        placement: { xPosition: 'before', yPosition: 'below' },
        tourLabel: 'Employees quick actions',
        createdAt: '2025-08-27',
        backdropConfig: { offset: 10 },
      },
      {

        anchorId: 'add-employee-btn',
        title: 'Add new employee',
        content: {
          shortDescription: '<p>22222Use this shortcut to hccc ggg open the employee creator.</p>',
        },
        enableBackdrop: true,
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        isAsync: true,
        placement: { xPosition: 'before', yPosition: 'below' },
        tourLabel: 'Employees quick actions',
        createdAt: '2025-09-27',
        backdropConfig: { offset: 10 },
      },
    ],
  },
  {
    id: 'employees-list-tour-3',
    feature: 'test',
    label: 'Test tour 2025',
    tags: ['employees', 'list', 'overview'],
    demoReady: false,
    visibleInQuickGuide: false,
    createdAt: '2025-10-17',
    order: 1,
    steps: [
      {
        anchorId: 'employees-list-intro',
        title: 'Overview',
        route: 'main/employees/list',
        delayAfterNavigation: 400,
        content: {
          shortDescription:
            '<p>This short tutorial walks you through <b>the employees list</b> so you can quickly discover every option available here. Navigate using the buttons below or by clicking on the highlighted elements.</p><p>You can also open this tutorial anytime by clicking the "Start tutorial" button.</p>',
        },
        enableBackdrop: true,
        tourLabel: 'Test overview',
        createdAt: '2025-10-17',
      },
      {
        anchorId: 'employee_personal_information_set_status',
        title: 'Employee status',
        content: {
          shortDescription: `
    <div style="font-size:13px; line-height:1.45; max-width:640px;">
      <p style="margin:0 0 6px;"><span style="font-weight:600;">Action:</br></span> Set or review the employee’s status. Some statuses are automatic; some can be pinned.</p>
      <p style="margin:0 0 8px;"><span style="font-weight:600;">Scenario:</br></span> Employees quick actions.</p>
      <figure style="margin:8px 0 0;">
        <img src="assets/tours/employee-status-demo.gif" alt="How to set employee status" style="max-width:100%; border-radius:10px;"/>
        <figcaption style="font-size:11px; opacity:.75; margin-top:4px;">Tip: you can “pin” a status by setting a future end date.</figcaption>
      </figure>
    </div>
  `,
          longDescription: `
    <div style="font-size:13px; line-height:1.45; max-width:640px;">
      <p style="margin:0 0 6px;"><span style="font-weight:600;"></br>How statuses work</span></p>
      <p style="margin:0 0 6px;">Statuses are computed in a strict priority order. The first matching rule wins:</p>
      <ol style="margin:0 0 10px 18px; padding:0;">
        <li><span style="font-weight:600;">BLOCKED</span> — always wins.</li>
        <li><span style="font-weight:600;">SUSPENDED</span> — stays until its end date; afterwards flips to <em>ACTIVE</em>.</li>
        <li><span style="font-weight:600;">RETIRED</span> — age ≥ 70 and not force-active.</li>
        <li><span style="font-weight:600;">CANDIDATE</span> — no known hire dates yet.</li>
        <li><span style="font-weight:600;">INACTIVE</span> — ≥ 2 years since the latest hire start/end and not force-active.</li>
        <li><span style="font-weight:600;">ACTIVE</span> — default fallback.</li>
      </ol>

      <div style="border:1px solid #e5e7eb; border-radius:10px; padding:10px; margin:0 0 10px;">
        <div style="display:grid; grid-template-columns: 120px 1fr; gap:6px;">
          <div><span style="font-weight:600;">BLOCKED</span></div>
          <div>Hard lock. Never auto-overridden.</div>

          <div><span style="font-weight:600;">SUSPENDED</span></div>
          <div>Frozen until its <em>end date</em>; afterwards flips to <em>ACTIVE</em>.</div>

          <div><span style="font-weight:600;">RETIRED</span></div>
          <div>Age ≥ 70 and not force-active.</div>

          <div><span style="font-weight:600;">CANDIDATE</span></div>
          <div>No hire dates yet. Ignores force-active.</div>

          <div><span style="font-weight:600;">INACTIVE</span></div>
          <div>≥ 2 years since the latest hire start/end and not force-active.</div>

          <div><span style="font-weight:600;">ACTIVE</span></div>
          <div>Default. A future end date keeps it force-active.</div>
        </div>
      </div>

      <p style="margin:0 0 6px;"><span style="font-weight:600;">Natural next step</span></p>
      <ul style="margin:0 0 0 18px; padding:0;">
        <li>Need to “freeze” the status?
          <ul style="margin:4px 0 6px 16px;">
            <li><span style="font-weight:600;">BLOCKED</span> for a hard lock.</li>
            <li><span style="font-weight:600;">SUSPENDED</span> with a future end date to pause until that date.</li>
            <li><span style="font-weight:600;">ACTIVE</span> with a future end date to prevent Retired/Inactive.</li>
          </ul>
        </li>
        <li style="margin-top:4px;">No hire history yet? Add a hire to leave the <em>Candidate</em> state.</li>
      </ul>
    </div>
  `,
          impactOnSystem: `
    <div style="font-size:13px; line-height:1.45; max-width:640px;">
      <p style="margin:0 0 6px;"><span style="font-weight:600;">Impact on the system</span></p>
      <ul style="margin:0 0 0 18px; padding:0;">
        <li><span style="font-weight:600;">BLOCKED</span> and <span style="font-weight:600;">SUSPENDED</span> are treated as not assignable and highlighted in availability views.</li>
        <li><span style="font-weight:600;">RETIRED</span>/<span style="font-weight:600;">INACTIVE</span> can be filtered out in searches and bulk actions.</li>
        <li><span style="font-weight:600;">CANDIDATE</span> usually means no hire recorded yet; some actions may be limited.</li>
      </ul>
    </div>
  `,
          risks: `
    <div style="font-size:13px; line-height:1.45; max-width:640px;">
      <p style="margin:0 0 6px;"><span style="font-weight:600;">Risks & gotchas</span></p>
      <ul style="margin:0 0 0 18px; padding:0;">
        <li><span style="font-weight:600;">Force-active</span> doesn’t prevent <em>Candidate</em> if there are no hire dates.</li>
        <li>When <span style="font-weight:600;">SUSPENDED</span> expires, it flips to <em>ACTIVE</em> immediately.</li>
        <li><span style="font-size:12px;">Make sure Suspended always has an end date for accuracy.</span></li>
      </ul>
    </div>
  `,
        },
        enableBackdrop: true,
        route: 'main/employees/details/add/personal-information',
        delayAfterNavigation: 400,
        isAsync: true,
        placement: { xPosition: 'before', yPosition: 'below' },
        tourLabel: 'Employees quick actions',
        createdAt: '2025-10-17',
        backdropConfig: { offset: 10 },
      },
    ],
  },
];
